import React, {useContext} from "react";
import {Hidden, TableRow} from "@mui/material";
import {styled} from "@mui/material/styles";
import {withStyles} from "@mui/styles";
import {FormattedMessage} from "react-intl";
import {useSnackbar} from "notistack";
import ListingCheckbox from "components/Product/Table/ListingCheckbox";
import ListingTableCell from "components/Product/Table/ListingTableCell";
import ListingProductImageCell from "components/Product/Table/ListingProductImageCell";
import ListingArticleCell from "components/Product/Table/ListingArticleCell";
import ListingMobileArticleCell from "components/Product/Table/ListingMobileArticleCell";
import ListingPriceCell from "components/Product/Table/ListingPriceCell";
import ListingSalesCell, {computeTotalSales} from "components/Product/Table/ListingSalesCell";
import ListingStockCell from "components/Product/Table/ListingStockCell";
import listStyle from "assets/jss/views/product/listStyle";
import {fetch} from "utils/dataAccess";
import {getFirstProductImage} from "utils/getFirstProductImage";
import {getTranslation} from "domain/helpers/translations";
import {sortOn} from "utils/sortOn";
import {MerchandisingContext} from "contexts/merchandisingContext";
import {StoreContext} from "contexts/storeContext";
import {Authorizations, hasAuthorization} from "utils/authorizations";
import {GLOBAL_NAVIGATION_ID, getContextualRank} from "utils/contextualRank";

// checkbox vertically centered next to the image column, as in the design
const ImageAndCheckbox = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
});

function MerchandisingRow({product, classes, isSelected, handleSelectSingle}) {
    const navParam = useContext(MerchandisingContext);
    const store = useContext(StoreContext);
    const currency = store.getState()?.currentOrganization?.retrieved?.currency;
    const {enqueueSnackbar} = useSnackbar();

    // mirrors the API's EDIT_PRODUCT voter, like the product list rank field
    const userAuthorizations = store.getState()?.authentication?.authorizations ?? [];
    const canEditRanking = hasAuthorization(userAuthorizations, Authorizations.MARKETPLACE_PRODUCT_MANAGEMENT)
        || hasAuthorization(userAuthorizations, Authorizations.MARKETPLACE_PRODUCT_EDIT);

    // the id of the navigation node the list is scoped to ("global" = no scope)
    const navigationId = navParam.navigationItem ? navParam.navigationItem.id : GLOBAL_NAVIGATION_ID;

    const saveRank = async (newRanking) => {
        const headers = new Headers();
        headers.set('Accept', 'application/json');

        const body = {
            productId: product.id,
            // rank 0 removes the product from the ranking
            rank: newRanking ?? 0,
            navigationType: GLOBAL_NAVIGATION_ID,
        };

        if (navParam.navigationType) {
            body.navigationType = navParam.navigationType;
            body.navigationValue = navParam.navigationValue;
        }

        const response = await fetch("/merchandising/rank", {method: "POST", body: JSON.stringify(body), headers});

        if (!response.ok) {
            enqueueSnackbar(<FormattedMessage id={"merchandising.ranking.card.error"}/>, {variant: 'error'});
            throw new Error('Rank update failed');
        }

        enqueueSnackbar(<FormattedMessage id={"merchandising.ranking.card.success"}/>, {variant: 'success'});
    };

    // same guard as the product list: a click that selects text is not a navigation;
    // the merchandising context (navigation, filters) only lives in memory, so the
    // product page always opens in a new tab
    const handleShowProduct = () => {
        if (window.getSelection().toString().length > 0) {
            return;
        }

        window.open(`/retailers/show/${product.retailerId}/products/show/${product.id}`, '_blank');
    };

    return (
        <TableRow
            onClick={handleShowProduct}
            hover
            tabIndex={-1}
            aria-checked={isSelected}
            selected={isSelected}
            classes={{hover: classes.tableRowHover}}
        >
            <ListingTableCell>
                <ImageAndCheckbox>
                    <ListingCheckbox
                        checked={isSelected}
                        onClick={(event) => {
                            event.stopPropagation();
                            handleSelectSingle(event, product.id);
                        }}
                    />
                    <ListingProductImageCell
                        // the field's value lives in local state: remount when the
                        // navigation scope changes so it shows the rank of that scope
                        key={navigationId}
                        showRanking
                        ranking={getContextualRank(product.rankings, navigationId)}
                        onRankingSave={canEditRanking ? saveRank : undefined}
                        src={product.pictures.length > 0 ? getFirstProductImage(product.pictures) : null}
                        alt="product_image"
                        groupedSearchImpression={product.groupedSearchImpression}
                    />
                </ImageAndCheckbox>
            </ListingTableCell>
            <ListingTableCell>
                <Hidden smDown>
                    <ListingArticleCell
                        name={getTranslation(product).name}
                        isEikow={product.eikow}
                        brandName={product.brand?.name}
                        sku={product.sku}
                        eshopId={product.eshopId}
                        categories={product.categories
                            .map((category) => ({id: category.id, name: getTranslation(category).name, draft: category.draft}))
                            .sort(sortOn("name"))}
                        updatedAt={product.updatedAt}
                        manualUpdatedAt={product.manualUpdatedAt}
                        manualUpdatedBy={product.manualUpdatedBy}
                    />
                </Hidden>
                <Hidden smUp>
                    <ListingMobileArticleCell
                        name={getTranslation(product).name}
                        isEikow={product.eikow}
                        brandName={product.brand?.name}
                        sku={product.sku}
                        active={product.status}
                        validated={product.validated}
                        hasPushError={product.hasPushError}
                        validationErrors={product.validationErrors}
                        ctr={product.ctr}
                        price={product.lowestPrice}
                        previousPrice={product.highestPrice}
                        currency={currency}
                        priceCompetitiveness={product.priceCompetitiveness}
                        totalSales={computeTotalSales(product)}
                        stocks={(product.variations ?? []).map((variation) => ({
                            id: variation.id,
                            label: variation.name,
                            stock: variation.stock,
                        }))}
                    />
                </Hidden>
            </ListingTableCell>
            <Hidden smDown>
                <ListingTableCell>
                    <ListingPriceCell
                        price={product.lowestPrice}
                        previousPrice={product.highestPrice}
                        currency={currency}
                        priceCompetitiveness={product.priceCompetitiveness}
                    />
                </ListingTableCell>
            </Hidden>
            <Hidden smDown>
                <ListingTableCell>
                    <ListingSalesCell
                        onlineSales={product.onlineSales}
                        totalSales={computeTotalSales(product)}
                        ctr={product.ctr}
                    />
                </ListingTableCell>
            </Hidden>
            <Hidden smDown>
                <ListingTableCell>
                    <ListingStockCell
                        active={product.status}
                        validated={product.validated}
                        hasPushError={product.hasPushError}
                        validationErrors={product.validationErrors}
                        siteUrl={product.status ? product.url : null}
                        stocks={(product.variations ?? []).map((variation) => ({
                            id: variation.id,
                            label: variation.name,
                            stock: variation.stock,
                        }))}
                    />
                </ListingTableCell>
            </Hidden>
        </TableRow>
    );
}

export default withStyles(listStyle)(MerchandisingRow);
